import React, { useEffect, useRef, useState } from 'react'
import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { PenSubpathRange } from '@/lib/elements/types/pen-subpath-range'
import { parseSvgPathToAnchors } from '@/lib/elements/pen-path-utils'
import type { NodeEditOverlayShapeGeom } from '../types/node-edit-overlay-props'
import type { NodeDragState } from '../types/node-edit-targets'

/**
 * The overlay's working copy of the path — anchors, subpaths, node selection, the active drag and
 * the live shape geometry — kept in refs so pointer handlers see it synchronously, and re-synced
 * from the `shape` prop whenever no drag is in progress.
 */
export function useNodePathState(shape: ShapeData, isPenEditMode: boolean) {
  // Parse path on first render; only re-parse externally when not dragging
  const initialParsed = React.useMemo(
    () =>
      shape.path
        ? parseSvgPathToAnchors(shape.path)
        : { points: [], isClosed: false, subpaths: [] },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const pointsRef = useRef<PenAnchorPoint[]>(initialParsed.points)
  const isClosedRef = useRef(initialParsed.isClosed)
  // One entry per disjoint contour (e.g. a glyph's inner counter, or several glyphs merged into
  // one compound path from text-to-shape) — every "is this closed / what wraps to what" question
  // is scoped to the subpath containing the point in question, never the flat array as a
  // whole. A traditional single-contour pen path is just the length-1 case.
  const subpathsRef = useRef<PenSubpathRange[]>(initialParsed.subpaths)
  const [, forceUpdate] = useState(0)
  const selectedIndicesRef = useRef<Set<number>>(new Set())
  // Tracks whether the current selection was built via shift+click, so a single
  // shift-selected node can render as "selected" (white) even before a second
  // node is added — giving the user feedback that the first click registered.
  const multiSelectActiveRef = useRef(false)

  const dragRef = useRef<NodeDragState | null>(null)

  // Tracks shape x/y/w/h locally so the overlay can expand it synchronously during
  // a drag without waiting for the prop to round-trip through React state.
  const shapeGeomRef = useRef<NodeEditOverlayShapeGeom>({
    x: shape.x,
    y: shape.y,
    width: shape.width,
    height: shape.height,
  })

  // Clear multi-selection when entering/leaving pen-edit mode so stale indices
  // (which may shift when nodes are added or removed) never drive a multi-drag.
  useEffect(() => {
    selectedIndicesRef.current = new Set()
    multiSelectActiveRef.current = false
    forceUpdate((n) => n + 1)
  }, [isPenEditMode])

  // Re-parse when shape.path changes externally (e.g. undo), but not during drag
  const prevPathRef = useRef(shape.path)

  if (shape.path !== prevPathRef.current && !dragRef.current) {
    prevPathRef.current = shape.path
    if (shape.path) {
      const { points, isClosed, subpaths } = parseSvgPathToAnchors(shape.path)

      pointsRef.current = points
      isClosedRef.current = isClosed
      subpathsRef.current = subpaths
    }

    // Node indices are no longer valid after an external path change (e.g. undo/redo).
    selectedIndicesRef.current = new Set()
    multiSelectActiveRef.current = false
  }

  // Keep shapeGeomRef in sync with the prop when not dragging (e.g. after undo or external resize).
  if (!dragRef.current) {
    shapeGeomRef.current = { x: shape.x, y: shape.y, width: shape.width, height: shape.height }
  }

  return {
    pointsRef,
    isClosedRef,
    subpathsRef,
    forceUpdate,
    selectedIndicesRef,
    multiSelectActiveRef,
    dragRef,
    shapeGeomRef,
  }
}

export type NodePathState = ReturnType<typeof useNodePathState>
